import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { ThemeToggleCompact } from '@/components/ui/ThemeToggle';
import HeaderAuth from '@/components/auth/HeaderAuth';

export function LandingHeader() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (sectionId: string) => {
    setIsMobileMenuOpen(false);

    const currentPath = location.pathname;
    const locale = i18n.language;
    const isHomepage = currentPath === `/${locale}` || currentPath === `/${locale}/`;

    if (isHomepage) {
      scrollToSection(sectionId);
    } else {
      navigate(`/${locale}#${sectionId}`);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleUpdatesClick = () => {
    setIsMobileMenuOpen(false);
    const locale = i18n.language;
    navigate(`/${locale}/updates#updates-top`);
    setTimeout(() => {
      const element = document.getElementById('updates-top');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <>
      <header className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? 'bg-[rgb(var(--background))]/95 dark:bg-[rgb(var(--background-secondary))]/95 backdrop-blur-md shadow-sm dark:shadow-2xl'
          : 'bg-[rgb(var(--background))]/80 dark:bg-[rgb(var(--background))]/70 backdrop-blur-md'
      } border-b border-gray-200/10 dark:border-white/10`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
                <img src="/claroclaim-two-color.svg" alt="Claroclaim Logo" className="h-8 w-auto dark:hidden" />
                <img src="/claoroclaim-white-color.svg" alt="Claroclaim Logo" className="h-8 w-auto hidden dark:block" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-10">
              <button
                onClick={() => handleNavClick('how-it-works')}
                className="text-[rgb(var(--foreground-muted))] hover:text-[rgb(var(--foreground))] dark:hover:text-white transition-colors relative group cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary-500 rounded-lg px-2 py-1"
              >
                {t('navigation.howItWorks', 'Wie es funktioniert')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-blue-400 transition-all duration-300 group-hover:w-full"></span>
              </button>
              <button
                onClick={() => handleNavClick('case-details')}
                className="text-[rgb(var(--foreground-muted))] hover:text-[rgb(var(--foreground))] dark:hover:text-white transition-colors relative group cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary-500 rounded-lg px-2 py-1"
              >
                {t('navigation.theCase', 'Der Fall')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-blue-400 transition-all duration-300 group-hover:w-full"></span>
              </button>
              <button
                onClick={() => handleNavClick('faq-videos')}
                className="text-[rgb(var(--foreground-muted))] hover:text-[rgb(var(--foreground))] dark:hover:text-white transition-colors relative group cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary-500 rounded-lg px-2 py-1"
              >
                {t('navigation.faq', 'FAQ')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-blue-400 transition-all duration-300 group-hover:w-full"></span>
              </button>
              <button
                onClick={handleUpdatesClick}
                className="text-[rgb(var(--foreground-muted))] hover:text-[rgb(var(--foreground))] dark:hover:text-white transition-colors relative group cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary-500 rounded-lg px-2 py-1"
              >
                {t('navigation.updates', 'Updates')}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-blue-400 transition-all duration-300 group-hover:w-full"></span>
              </button>
            </nav>

            {/* Right side - Desktop: all items, Mobile: just burger */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* Hide auth/language/theme on mobile - they go in burger menu */}
              <div className="hidden md:flex items-center space-x-4">
                <HeaderAuth />
                <LanguageSelector />
                <ThemeToggleCompact />
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-[rgb(var(--foreground))] hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary-500"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay - outside header for proper z-stacking */}
      <div
        className={`md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile menu panel - outside header for proper z-stacking */}
      <div
        className={`md:hidden fixed top-16 left-0 right-0 bg-[rgb(var(--background))] dark:bg-[rgb(var(--background-secondary))] border-b border-gray-200 dark:border-gray-700 shadow-lg z-50 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <nav className="flex flex-col py-4 px-4 space-y-1">
          <button
            onClick={() => handleNavClick('how-it-works')}
            className="w-full text-left px-4 py-3 text-lg font-medium text-[rgb(var(--foreground))] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
          >
            {t('navigation.howItWorks', 'Wie es funktioniert')}
          </button>
          <button
            onClick={() => handleNavClick('case-details')}
            className="w-full text-left px-4 py-3 text-lg font-medium text-[rgb(var(--foreground))] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
          >
            {t('navigation.theCase', 'Der Fall')}
          </button>
          <button
            onClick={() => handleNavClick('faq-videos')}
            className="w-full text-left px-4 py-3 text-lg font-medium text-[rgb(var(--foreground))] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
          >
            {t('navigation.faq', 'FAQ')}
          </button>
          <button
            onClick={handleUpdatesClick}
            className="w-full text-left px-4 py-3 text-lg font-medium text-[rgb(var(--foreground))] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
          >
            {t('navigation.updates', 'Updates')}
          </button>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

          {/* Auth in mobile menu */}
          <div className="px-4 py-3">
            <HeaderAuth />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

          {/* Language and Theme in mobile menu */}
          <div className="flex items-center justify-between px-4 py-2">
            <span className="text-sm text-[rgb(var(--foreground-muted))]">
              {t('navigation.language', 'Sprache')}
            </span>
            <LanguageSelector />
          </div>
          <div className="flex items-center justify-between px-4 py-2">
            <span className="text-sm text-[rgb(var(--foreground-muted))]">
              {t('navigation.theme', 'Design')}
            </span>
            <ThemeToggleCompact />
          </div>
        </nav>
      </div>
    </>
  );
}
